import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { FormattedMessage } from 'react-intl';
import dayjs from 'dayjs';
import type { PreviewVisit } from '../../pages/booking-page.hooks';
import { useBookingReservationSummary } from './booking-reservation-summary.hooks';

type Props = {
  previewVisit: PreviewVisit | undefined;
  setPreviewVisit: (v: PreviewVisit | undefined) => void;
};

const SummaryRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box>
    <Typography variant="body2" color="text.secondary">
      {label}:
    </Typography>
    <Typography variant="body1" fontWeight="medium">
      {value}
    </Typography>
  </Box>
);

export const BookingReservationSummary = ({ previewVisit, setPreviewVisit }: Props) => {
  const {
    selectedDate,
    selectedService,
    createVisitMutation,
    error,
    success,
    canConfirm,
    handleConfirm,
    t,
  } = useBookingReservationSummary(previewVisit, setPreviewVisit);

  return (
    <>
      {previewVisit && (
        <Card sx={{ bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.main' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              <FormattedMessage id="customerAuth.booking.summary" />
            </Typography>
            <Stack spacing={1}>
              <SummaryRow
                label={t('booking.service')}
                value={previewVisit.serviceName || selectedService?.serviceName}
              />
              <SummaryRow
                label={t('booking.date')}
                value={dayjs(selectedDate).format('DD.MM.YYYY')}
              />
              <SummaryRow
                label={t('booking.time')}
                value={`${previewVisit.startTime} - ${previewVisit.endTime}`}
              />
              {selectedService?.blocks && (
                <SummaryRow
                  label={t('booking.duration')}
                  value={t('booking.durationMinutes', {
                    minutes: selectedService.blocks.reduce((sum, b) => sum + b.duration, 0),
                  })}
                />
              )}
            </Stack>

            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              onClick={handleConfirm}
              disabled={!canConfirm || createVisitMutation.isPending}
              startIcon={
                createVisitMutation.isPending ? <CircularProgress size={20} /> : <CheckCircle />
              }
              sx={{ mt: 3 }}
            >
              {createVisitMutation.isPending ? (
                <FormattedMessage id="customerAuth.booking.creating" />
              ) : (
                <FormattedMessage id="customerAuth.booking.confirm" />
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {success && (
        <Alert severity="success" sx={{ mt: 2 }}>
          <FormattedMessage id="customerAuth.booking.successMessage" />
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </>
  );
};
